import {
  followCompanies,
  unfollowCompanies,
  queryFollowedCompanies,
  setFollowFrequency,
} from '@/api/watchlist';

// Shared in-flight loadFollowed promise so N FollowButtons mounting together issue one request.
let followedInflight = null;

const DEFAULT_FREQUENCY = 'daily';

// followedIds is the flat set of domain_search_id the user follows. frequencies maps each one to its
// own alert cadence (instant/daily/weekly) — a company lives in exactly one cadence bucket. Toggles are optimistic.
const state = {
  followedIds: [],
  frequencies: {},
  loaded: false,
};

const mutations = {
  // companies: [{ domain_search_id, frequency }] from the server.
  SET_FOLLOWED(state, companies) {
    const list = Array.isArray(companies) ? companies : [];
    state.followedIds = list.map((c) => c.domain_search_id).filter(Boolean);
    const map = {};
    list.forEach((c) => {
      if (c && c.domain_search_id) map[c.domain_search_id] = c.frequency || DEFAULT_FREQUENCY;
    });
    state.frequencies = map;
    state.loaded = true;
  },
  // Reassign the object (not mutate a key) so Vue 2 tracks added/changed cadences reactively.
  SET_FREQUENCY(state, { id, frequency }) {
    if (id && frequency) state.frequencies = { ...state.frequencies, [id]: frequency };
  },
  ADD_FOLLOWED(state, { id, frequency }) {
    if (!id) return;
    if (!state.followedIds.includes(id)) state.followedIds.push(id);
    state.frequencies = { ...state.frequencies, [id]: frequency || DEFAULT_FREQUENCY };
  },
  REMOVE_FOLLOWED(state, id) {
    const i = state.followedIds.indexOf(id);
    if (i !== -1) state.followedIds.splice(i, 1);
    if (id in state.frequencies) {
      const next = { ...state.frequencies };
      delete next[id];
      state.frequencies = next;
    }
  },
  // Wipe follow state on profile switch / logout so the next user doesn't inherit this one's follows.
  RESET(state) {
    state.followedIds = [];
    state.frequencies = {};
    state.loaded = false;
  },
};

// Bearer auth resolves profile_id from the request body (see authorisationHandler) — every call must send it.
const profileId = (rootState) => rootState.user && rootState.user.currentId && rootState.user.currentId.profile_id;

const actions = {
  // Hydrate the followed set once; de-dupes concurrent callers and never rejects (follow UI is non-critical).
  async loadFollowed({ commit, state, rootState }) {
    if (state.loaded) return state.followedIds;
    if (followedInflight) return followedInflight;
    const profile_id = profileId(rootState);
    followedInflight = (async () => {
      try {
        const res = await queryFollowedCompanies({ profile_id });
        const data = (res && res.data) || {};
        // Prefer the per-company shape; fall back to a flat id list (all default cadence).
        const companies = Array.isArray(data.companies)
          ? data.companies
          : (data.domain_search_ids || []).map((id) => ({ domain_search_id: id, frequency: DEFAULT_FREQUENCY }));
        commit('SET_FOLLOWED', companies);
        return state.followedIds;
      } catch (e) {
        return [];
      } finally {
        followedInflight = null;
      }
    })();
    return followedInflight;
  },
  // Move one followed company to a new cadence bucket. Optimistic, reverts on failure.
  async setFrequency({ commit, state, rootState }, { id, frequency } = {}) {
    if (!id || !frequency) return;
    const prev = state.frequencies[id] || DEFAULT_FREQUENCY;
    if (frequency === prev) return;
    commit('SET_FREQUENCY', { id, frequency });
    try {
      await setFollowFrequency({ domain_search_id: id, frequency, profile_id: profileId(rootState) });
    } catch (e) {
      commit('SET_FREQUENCY', { id, frequency: prev });
      throw e;
    }
  },
  async follow({ commit, rootState }, { id, watchlist_id } = {}) {
    if (!id) return;
    commit('ADD_FOLLOWED', { id, frequency: DEFAULT_FREQUENCY });
    try {
      await followCompanies({ companies: [id], watchlist_id, profile_id: profileId(rootState) });
    } catch (e) {
      commit('REMOVE_FOLLOWED', id);
      throw e;
    }
  },
  async unfollow({ commit, state, rootState }, { id, watchlist_id } = {}) {
    if (!id) return;
    const prevFrequency = state.frequencies[id] || DEFAULT_FREQUENCY;
    commit('REMOVE_FOLLOWED', id);
    try {
      await unfollowCompanies({ companies: [id], watchlist_id, profile_id: profileId(rootState) });
    } catch (e) {
      commit('ADD_FOLLOWED', { id, frequency: prevFrequency });
      throw e;
    }
  },
};

const getters = {
  isFollowed: (state) => (id) => state.followedIds.includes(id),
  frequencyOf: (state) => (id) => state.frequencies[id] || DEFAULT_FREQUENCY,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
